import type React from "react";
import { useState } from "react";
import { Box, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";

// Types
import type { VenueData, IContactData } from "./types/venues.types";
import {
  mapContactDataToVenueContact,
  DOCUMENT_TYPES,
} from "./view-venue/utils/venue.utils";

// Hooks
import { useVenueData } from "./view-venue/hooks/UseVenueData";
import { useBookings } from "./view-venue/hooks/UseBooking";
import { useContactSearch } from "./view-venue/hooks/UseContactSearch";
import { useSimilarVenues } from "./view-venue/hooks/UseSimilarVenues";
import { useMergeVenues } from "./view-venue/hooks/UseMergeVenues";

// Components
import LoadingState from "./view-venue/components/LoadingState";
import ErrorState from "./view-venue/components/ErrorState";
import VenueHeader from "./view-venue/components/VenueHeader";
import VenueFooter from "./view-venue/components/VenueFooter";
import VenueLayoutsGrid from "./view-venue/components/VenueLayoutGrid";
import VenueDetailsTab from "./view-venue/components/VenueDetailsTab";
import AddContactModal from "./view-venue/components/AddContactModal";
import VenueMergeDialog from "./view-venue/components/VenueMergeDialog";

// These should be imported from your existing codebase
import InfoCard from "./view-venue/components/InfoCard";
import InfoRow from "./view-venue/components/InfoRow";
import ViewVenuePhotos from "./tabs/ViewVenuePhotos";
import apiService, { BaseURL } from "../../service/Api";
import { tokens } from "../../theme";
import formatDate from "../../utils/FormatDate";
import ContactDetailsDialog from "./view-venue/components/ContactDetailsDialog";

// Layout images
import banquet from "../../assets/venue-layouts/banquet.png";
import theatre from "../../assets/venue-layouts/theatre.png";
import cocktail from "../../assets/venue-layouts/cocktail.jpg";
import classroom from "../../assets/venue-layouts/classroom.png";
import ViewVenueV2 from "./view-venue-v2/ViewVenue2";

interface ViewVenueProps {
  venueData?: VenueData;
}

const layoutImages: Record<string, string> = {
  banquet,
  theater: theatre,
  classroom,
  cocktail,
};

const ViewVenue: React.FC<ViewVenueProps> = ({
  venueData: defaultVenueData,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();

  const userPermissions = useSelector(
    (state: RootState) => state.auth.user?.permissions || [],
  );
  const canMerge = userPermissions.includes("venue.delete_venue");

  // Contact pagination
  const [contactPage, setContactPage] = useState(0);
  const [contactRowsPerPage, setContactRowsPerPage] = useState(5);

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [secondaryVenueId, setSecondaryVenueId] = useState<number | null>(null);
  const [secondaryVenueData, setSecondaryVenueData] =
    useState<VenueData | null>(null);
  const [secondaryVenueLoading, setSecondaryVenueLoading] = useState(false);

  // Custom hooks
  const { venueData, setVenueData, loading, error } = useVenueData({
    id,
    defaultVenueData,
    apiService,
  });

  const {
    bookings,
    totalBookings,
    bookingsLoading,
    page: bookingsPage,
    rowsPerPage: bookingsRowsPerPage,
    handleChangePage: handleBookingsPageChange,
    handleChangeRowsPerPage: handleBookingsRowsPerPageChange,
  } = useBookings({ venueId: id, apiService });

  const {
    openContactModal,
    setOpenContactModal,
    contactSearch,
    setContactSearch,
    searchResults,
    isSearching,
    savingContact,
    contactModalStep,
    setContactModalStep,
    searchAttempted,
    newContactForm,
    setNewContactForm,
    handleAddExistingContact,
    handleAddNewContact,
    resetContactModal,
  } = useContactSearch({ venueId: id, venueData, setVenueData, apiService });

  const {
    similarVenues,
    loading: similarVenuesLoading,
    refetch: refetchSimilarVenues,
  } = useSimilarVenues({ venueId: id, apiService });

  const { mergeVenues, merging } = useMergeVenues({
    apiService,
    onSuccess: (updatedVenue) => {
      setVenueData(updatedVenue);
      setMergeDialogOpen(false);
      setSecondaryVenueData(null);
      setSecondaryVenueId(null);
      refetchSimilarVenues();
    },
  });

  // Merge handlers
  const handleMergeClick = async (targetVenueId: number) => {
    setSecondaryVenueId(targetVenueId);
    setMergeDialogOpen(true);
    setSecondaryVenueLoading(true);
    try {
      const response = await apiService.get<VenueData>(
        `/api/venues/venue/${targetVenueId}/`,
      );
      setSecondaryVenueData(response.data);
    } catch {
      setSecondaryVenueData(null);
    } finally {
      setSecondaryVenueLoading(false);
    }
  };

  const handleMerge = (
    fieldSelections: Record<string, "primary" | "secondary">,
  ) => {
    if (venueData && secondaryVenueId) {
      mergeVenues(venueData.id, secondaryVenueId, fieldSelections);
    }
  };

  // Contact pagination handlers
  const handleChangeContactPage = (_event: unknown, newPage: number) => {
    setContactPage(newPage);
  };

  const handleChangeContactRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setContactRowsPerPage(Number.parseInt(event.target.value, 10));
    setContactPage(0);
  };

  const paginatedContacts = (venueData?.contacts || []).slice(
    contactPage * contactRowsPerPage,
    contactPage * contactRowsPerPage + contactRowsPerPage,
  );

  // Tab handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleContactRemove = (contactId: number) => {
    if (venueData) {
      setVenueData({
        ...venueData,
        contacts: venueData.contacts.filter((c) => c.id !== contactId),
      });
    }
  };

  const handleContactEdit = (updatedContact: IContactData) => {
    if (venueData) {
      const venueId = Number.parseInt(id || "0");
      const normalizedContact = mapContactDataToVenueContact(
        updatedContact,
        venueId,
      );
      setVenueData({
        ...venueData,
        contacts: venueData.contacts.map((c) =>
          c.id === updatedContact.id ? normalizedContact : c,
        ),
      });
    }
  };

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Error state
  if (error || !venueData) {
    return <ErrorState error={error} onBackClick={() => navigate("/venues")} />;
  }

  const ContactDetailsWithVenue: React.FC<{
    contact: IContactData;
    onEdit: (updatedContact: IContactData) => void;
    inline?: boolean;
    showActionsInline?: boolean;
  }> = (props) => (
    <ContactDetailsDialog
      {...props}
      venueData={venueData}
      onRemove={handleContactRemove}
    />
  );

  const tabContent = [
    // Venue Details Tab
    <VenueDetailsTab
      key="venue-details"
      venueData={venueData}
      bookings={bookings}
      totalBookings={totalBookings}
      bookingsLoading={bookingsLoading}
      bookingsPage={bookingsPage}
      bookingsRowsPerPage={bookingsRowsPerPage}
      onBookingsPageChange={handleBookingsPageChange}
      onBookingsRowsPerPageChange={handleBookingsRowsPerPageChange}
      onBookingClick={(bookingId) => navigate(`/bookings/${bookingId}`)}
      similarVenues={similarVenues}
      similarVenuesLoading={similarVenuesLoading}
      onSimilarVenueClick={(venueId) => navigate(`/venues/view/${venueId}`)}
      onMergeClick={handleMergeClick}
      canMerge={canMerge}
      paginatedContacts={paginatedContacts}
      contactPage={contactPage}
      contactRowsPerPage={contactRowsPerPage}
      onContactPageChange={handleChangeContactPage}
      onContactRowsPerPageChange={handleChangeContactRowsPerPage}
      onAddContactClick={() => setOpenContactModal(true)}
      onContactEdit={handleContactEdit}
      formatDate={formatDate}
      InfoCard={InfoCard}
      InfoRow={InfoRow}
      ContactDetailsDialog={ContactDetailsWithVenue}
    />,

    ...DOCUMENT_TYPES.map((docType) => (
      <Box key={docType}>
        <ViewVenuePhotos files={venueData.files} document_type={docType} />
      </Box>
    )),

    <VenueLayoutsGrid
      key="layouts-container"
      layouts={venueData.layouts}
      layoutImages={layoutImages}
    />,
  ];

  if (BaseURL?.includes("staging.muzukuru.com")) {
    return <ViewVenueV2 />;
  }
  return (
    <Box>
      <VenueHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onEditClick={() => navigate(`/venues/view/${id}/edit`)}
        primaryColor={colors.primary[100]}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
        >
          {tabContent[activeTab]}
        </motion.div>
      </AnimatePresence>

      <VenueFooter venueData={venueData} />

      <AddContactModal
        open={openContactModal}
        onClose={resetContactModal}
        contactModalStep={contactModalStep}
        setContactModalStep={setContactModalStep}
        contactSearch={contactSearch}
        setContactSearch={setContactSearch}
        searchResults={searchResults}
        isSearching={isSearching}
        savingContact={savingContact}
        searchAttempted={searchAttempted}
        newContactForm={newContactForm}
        setNewContactForm={setNewContactForm}
        onAddExistingContact={handleAddExistingContact}
        onAddNewContact={handleAddNewContact}
      />

      {venueData && (
        <VenueMergeDialog
          open={mergeDialogOpen}
          onClose={() => {
            setMergeDialogOpen(false);
            setSecondaryVenueData(null);
            setSecondaryVenueId(null);
          }}
          primaryVenue={venueData}
          secondaryVenue={secondaryVenueData}
          secondaryLoading={secondaryVenueLoading}
          onMerge={handleMerge}
          merging={merging}
        />
      )}
    </Box>
  );
};

export default ViewVenue;
